import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { ErrorState } from '../components/feedback/ErrorState';
import { useAuth } from '../hooks/useAuth.js';
import { useWorkspaceUsers } from '../hooks/useWorkspaceUsers.js';
import {
  useRevokeWorkspacePermission,
  useSaveWorkspacePermissionAdmins,
  useUpsertWorkspacePermission,
  useWorkspacePermissions,
} from '../hooks/useWorkspacePermissions.js';
import type { WorkspacePermission, WorkspacePermissionRole } from '../types/api.js';

/** 編集フォームの状態。パス・スタックは 1 行 1 件のテキストで持つ。 @graph-connects none */
interface GrantForm {
  email: string;
  gitAuthorName: string;
  role: WorkspacePermissionRole;
  allowedPaths: string;
  allowedStacks: string;
  isNew: boolean;
}

/** @graph-connects none */
function toForm(permission: WorkspacePermission): GrantForm {
  return {
    email: permission.email,
    gitAuthorName: permission.gitAuthorName,
    role: permission.role,
    allowedPaths: permission.allowedPaths.join('\n'),
    allowedStacks: permission.allowedStacks.join('\n'),
    isNew: false,
  };
}

/** @graph-connects none */
function emptyForm(): GrantForm {
  return {
    email: '',
    gitAuthorName: '',
    role: 'developer',
    allowedPaths: '',
    allowedStacks: '',
    isNew: true,
  };
}

/** 1 行 1 件のテキストを配列にする。空行は捨てる。 @graph-connects none */
function toLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/** エラーの文言は API（正本はドメインパッケージ）が返したものをそのまま見せる。 @graph-connects none */
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace MCP のスタック権限（cortex_stack_permissions）を配る・失効する画面。cortex_admin_workspace_permissions の管理者のみアクセス可能。配れる条件の判定は API 側にあり、ここは返ってきた文言をそのまま見せる
 * @graph-connects useAuth [calls] 現在のユーザー情報取得（isWorkspacePermissionAdmin チェック）
 * @graph-connects useWorkspacePermissions [calls] 権限と管理者の一覧取得
 * @graph-connects useUpsertWorkspacePermission [calls] 権限の登録・更新
 * @graph-connects useRevokeWorkspacePermission [calls] 権限の失効
 * @graph-connects useSaveWorkspacePermissionAdmins [calls] 管理者一覧の保存
 * @graph-connects useWorkspaceUsers [calls] メールアドレス入力の候補
 * @graph-connects useToast [calls] 結果通知
 */
export const Route = createFileRoute('/workspace-permissions')({
  component: WorkspacePermissionsPage,
});

/** @graph-connects none */
export function WorkspacePermissionsPage() {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data, isLoading, isError, refetch } = useWorkspacePermissions();
  const { data: workspaceUsers } = useWorkspaceUsers();
  const upsert = useUpsertWorkspacePermission();
  const revoke = useRevokeWorkspacePermission();
  const saveAdmins = useSaveWorkspacePermissionAdmins();
  const { showToast } = useToast();

  const [form, setForm] = useState<GrantForm | null>(null);
  const [adminDraft, setAdminDraft] = useState('');
  const [admins, setAdmins] = useState<string[] | null>(null);

  if (isAuthLoading || isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (user && !user.isWorkspacePermissionAdmin) {
    return (
      <ErrorState
        title={t('common.accessDeniedTitle')}
        message={t('workspacePermissions.accessDenied')}
      />
    );
  }

  if (isError || !data) {
    return (
      <ErrorState
        title={t('common.loadFailedTitle')}
        message={t('workspacePermissions.loadFailed')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const adminList = admins ?? data.admins;

  const handleSaveGrant = (): void => {
    if (!form) return;
    upsert.mutate(
      {
        email: form.email.trim(),
        gitAuthorName: form.gitAuthorName,
        role: form.role,
        allowedPaths: toLines(form.allowedPaths),
        allowedStacks: toLines(form.allowedStacks),
      },
      {
        onSuccess: (saved) => {
          showToast('success', t('workspacePermissions.saveGrantSuccess', { email: saved.email }));
          setForm(null);
        },
        onError: (error) => {
          showToast(
            'error',
            t('workspacePermissions.saveGrantError', { reason: errorMessage(error) }),
          );
        },
      },
    );
  };

  const handleRevoke = (email: string): void => {
    if (!window.confirm(t('workspacePermissions.revokeConfirm', { email }))) return;
    revoke.mutate(email, {
      onSuccess: (result) => {
        showToast(
          result.revoked ? 'success' : 'error',
          result.revoked
            ? t('workspacePermissions.revokeSuccess', { email })
            : t('workspacePermissions.revokeNotFound', { email }),
        );
      },
      onError: (error) => {
        showToast('error', t('workspacePermissions.revokeError', { reason: errorMessage(error) }));
      },
    });
  };

  const handleSaveAdmins = (): void => {
    saveAdmins.mutate(adminList, {
      onSuccess: () => {
        showToast('success', t('workspacePermissions.saveAdminsSuccess'));
        setAdmins(null);
      },
      onError: (error) => {
        showToast(
          'error',
          t('workspacePermissions.saveAdminsError', { reason: errorMessage(error) }),
        );
      },
    });
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('workspacePermissions.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('workspacePermissions.description')}
        </p>
      </header>

      {/* 管理者 */}
      <section className="glass-card p-6 space-y-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('workspacePermissions.adminsTitle')}
        </h2>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('workspacePermissions.adminsDescription')}
        </p>
        <div className="flex flex-wrap gap-2">
          {data.bootstrapAdmins.map((email) => (
            <span
              key={email}
              className="px-2 py-1 text-xs rounded bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]"
            >
              {email} · {t('workspacePermissions.adminsFixedLabel')}
            </span>
          ))}
          {adminList.map((email) => (
            <span
              key={email}
              className="px-2 py-1 text-xs rounded bg-[var(--color-bg-subtle)] text-[var(--color-text-primary)] flex items-center gap-2"
            >
              {email}
              <button
                type="button"
                aria-label={t('workspacePermissions.removeButton', { email })}
                className="text-[var(--color-text-secondary)]"
                onClick={() => {
                  setAdmins(adminList.filter((value) => value !== email));
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            list="workspace-permission-user-emails"
            value={adminDraft}
            placeholder={t('workspacePermissions.adminsAddPlaceholder')}
            aria-label={t('workspacePermissions.adminsAddPlaceholder')}
            className="flex-1 px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
            onChange={(event) => {
              setAdminDraft(event.target.value);
            }}
          />
          <Button
            variant="secondary"
            onClick={() => {
              const next = adminDraft.trim().toLowerCase();
              if (next === '' || adminList.includes(next)) return;
              setAdmins([...adminList, next]);
              setAdminDraft('');
            }}
          >
            {t('workspacePermissions.addButton')}
          </Button>
          <Button onClick={handleSaveAdmins} disabled={saveAdmins.isPending}>
            {t('workspacePermissions.saveAdmins')}
          </Button>
        </div>
      </section>

      {/* 権限一覧 */}
      <section className="glass-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('workspacePermissions.grantsTitle')}
          </h2>
          <Button
            variant="secondary"
            onClick={() => {
              setForm(emptyForm());
            }}
          >
            {t('workspacePermissions.newGrant')}
          </Button>
        </div>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('workspacePermissions.grantsDescription')}
        </p>

        {data.permissions.length === 0 ? (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('workspacePermissions.grantsEmpty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[var(--color-text-secondary)]">
                  <th className="py-2 pr-3">{t('workspacePermissions.columnEmail')}</th>
                  <th className="py-2 pr-3">{t('workspacePermissions.columnRole')}</th>
                  <th className="py-2 pr-3">{t('workspacePermissions.columnPaths')}</th>
                  <th className="py-2 pr-3">{t('workspacePermissions.columnStacks')}</th>
                  <th className="py-2 pr-3">{t('workspacePermissions.columnUpdated')}</th>
                  <th className="py-2">{t('workspacePermissions.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.permissions.map((permission) => (
                  <tr key={permission.email} className="border-t border-[var(--color-border)]">
                    <td className="py-2 pr-3 text-[var(--color-text-primary)]">
                      {permission.email}
                    </td>
                    <td className="py-2 pr-3">{permission.role}</td>
                    <td className="py-2 pr-3">
                      <ul>
                        {permission.allowedPaths.map((path) => (
                          <li key={path}>{path}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="py-2 pr-3">{permission.allowedStacks.join(', ')}</td>
                    <td className="py-2 pr-3">
                      {permission.updatedAt === undefined
                        ? '-'
                        : t('workspacePermissions.updatedBy', {
                            at: new Date(permission.updatedAt).toLocaleString(),
                            by: permission.updatedBy ?? '-',
                          })}
                    </td>
                    <td className="py-2 space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="underline text-[var(--color-text-secondary)]"
                        onClick={() => {
                          setForm(toForm(permission));
                        }}
                      >
                        {t('workspacePermissions.edit')}
                      </button>
                      <button
                        type="button"
                        className="underline text-[var(--color-text-secondary)]"
                        disabled={revoke.isPending}
                        onClick={() => {
                          handleRevoke(permission.email);
                        }}
                      >
                        {t('workspacePermissions.revoke')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 編集フォーム */}
      {form !== null && (
        <section className="glass-card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {form.isNew
              ? t('workspacePermissions.newGrant')
              : t('workspacePermissions.editGrant', { email: form.email })}
          </h2>

          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-secondary)]" htmlFor="wsperm-email">
              {t('workspacePermissions.emailLabel')}
            </label>
            <input
              id="wsperm-email"
              type="email"
              list="workspace-permission-user-emails"
              value={form.email}
              disabled={!form.isNew}
              placeholder={t('workspacePermissions.emailPlaceholder')}
              className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
              onChange={(event) => {
                setForm({ ...form, email: event.target.value });
              }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-secondary)]" htmlFor="wsperm-author">
              {t('workspacePermissions.gitAuthorNameLabel')}
            </label>
            <input
              id="wsperm-author"
              type="text"
              value={form.gitAuthorName}
              className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
              onChange={(event) => {
                setForm({ ...form, gitAuthorName: event.target.value });
              }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-secondary)]" htmlFor="wsperm-role">
              {t('workspacePermissions.roleLabel')}
            </label>
            <select
              id="wsperm-role"
              value={form.role}
              className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
              onChange={(event) => {
                setForm({ ...form, role: event.target.value as WorkspacePermissionRole });
              }}
            >
              <option value="developer">{t('workspacePermissions.roleDeveloper')}</option>
              <option value="viewer">{t('workspacePermissions.roleViewer')}</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-secondary)]" htmlFor="wsperm-paths">
              {t('workspacePermissions.allowedPathsLabel')}
            </label>
            <textarea
              id="wsperm-paths"
              rows={5}
              value={form.allowedPaths}
              className="w-full px-3 py-2 text-sm font-mono rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
              onChange={(event) => {
                setForm({ ...form, allowedPaths: event.target.value });
              }}
            />
            <p className="text-xs text-[var(--color-text-secondary)]">
              {t('workspacePermissions.allowedPathsHint')}
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-secondary)]" htmlFor="wsperm-stacks">
              {t('workspacePermissions.allowedStacksLabel')}
            </label>
            <textarea
              id="wsperm-stacks"
              rows={3}
              value={form.allowedStacks}
              className="w-full px-3 py-2 text-sm font-mono rounded border border-[var(--color-border)] bg-transparent text-[var(--color-text-primary)]"
              onChange={(event) => {
                setForm({ ...form, allowedStacks: event.target.value });
              }}
            />
            <p className="text-xs text-[var(--color-text-secondary)]">
              {t('workspacePermissions.allowedStacksHint')}
            </p>
          </div>

          {upsert.isError && (
            <p className="text-xs text-[var(--color-danger)]">{errorMessage(upsert.error)}</p>
          )}

          <div className="flex gap-2">
            <Button onClick={handleSaveGrant} disabled={upsert.isPending}>
              {t('workspacePermissions.saveGrant')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setForm(null);
              }}
            >
              {t('workspacePermissions.cancel')}
            </Button>
          </div>
        </section>
      )}

      <datalist id="workspace-permission-user-emails">
        {(workspaceUsers ?? []).map((candidate) => (
          <option key={candidate.id} value={candidate.email} />
        ))}
      </datalist>
    </div>
  );
}
